import React, { useState, useEffect } from 'react';
import {
  FiChevronDown,
  FiBriefcase,
  FiTarget,
  FiUser,
  FiCalendar,
  FiCheckCircle,
  FiDollarSign,
  FiActivity,
  FiClock,
  FiUsers,
  FiBarChart2,
  FiTrendingUp,
  FiShield,
  FiInfo,
  FiHome,
  FiTrendingUp as FiTrendingUpIcon,
  FiAlertTriangle,
  FiMap,
  FiPieChart,
  FiMail
} from 'react-icons/fi';
import Loading from '../../components/Loading';
import SidepanelTest from './SidepanelTest';
import OverviewTab from './OverviewTab';
import StakeholdersTab from './StakeholdersTab';
import ValueTab from './ValueTab';
import RisksTab from './RisksTab';
import ActionsTab from './ActionsTab';
import TimelineTab from './TimelineTab';
import CompetitiveTab from './CompetitiveTab';
import ExpansionTab from './ExpansionTab';
import ResearchTab from './ResearchTab';
import SignalsTab from './SignalsTab';
import { apiService } from '../../service/Api.js';
import { useAuth } from '../../context/AuthContext';
    
import { Chart, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler } from 'chart.js';
Chart.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);


const CompanyTest = () => {
  const [insightData, setInsightData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantId, setTenantId] = useState('');
  const [objectId, setObjectId] = useState('');
  const [insightType, setInsightType] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [expandSections, setExpandSections] = useState({});
  // Research tab state
  const [researchData, setResearchData] = useState(null);
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchError, setResearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [pageContent, setPageContent] = useState(null);

  // Signals tab state
  const [signalsData, setSignalsData] = useState(null);
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [signalsError, setSignalsError] = useState(null);
  
  const { isAppInstalled } = useAuth();
  
  console.log('isAppInstalled:', isAppInstalled);

  // Section toggling
  const toggleSection = (key) => {
    setExpandSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const Section = ({ title, iconComponent, children, collapsible, expanded, onToggle }) => (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-xl mb-6 p-6 transition-all duration-200">
      <div 
        className={`font-semibold text-sm mb-2 flex items-center gap-2 ${
          collapsible ? 'cursor-pointer' : ''
        }`}
        onClick={collapsible ? onToggle : undefined}
      >
        {iconComponent && React.createElement(iconComponent, { className: "w-4 h-4 text-gray-700" })} 
        {title}
        {collapsible && (
          <FiChevronDown
            className={`ml-auto w-5 h-5 text-gray-500 transition-transform duration-200 ${
              expanded ? 'rotate-0' : '-rotate-90'
            }`}
          />
        )}
      </div>
      {(!collapsible || expanded) && <div className="mt-4">{children}</div>}
    </div>
  );

  // Listen for messages from Gmail extension and Chrome extension page content
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('📄 [CompanyTest] Received message:', {
        origin: event.origin,
        type: event.data?.type,
        data: event.data
      });
      
      if (event.data && event.data.type === 'GMAIL_INSIGHTS_DATA') {
        console.log('📨 Received Gmail insights via postMessage:', event.data.data.insights.insight.aura_analysis);
        setInsightData(event.data.data.insights.insight.aura_analysis);
        setLoading(false);
      } else if (event.data.type === 'PAGE_CONTENT_DATA') {
        console.log('📄 [CompanyTest] Received page content data:', {
          url: event.data.data.url,
          bodyTextLength: event.data.data.bodyText?.length || 0,
        });
        setPageContent(event.data.data.bodyText);
      }
    };

    const handleChromeMessage = (message, sender, sendResponse) => {
      if (message.type === 'GMAIL_INSIGHTS_DATA') {
        console.log('📨 Received Gmail insights via Chrome messaging:', message);
        setInsightData(message.data);
        console.log("insightData from chrome messaging",insightData.data.insights.insight.aura_analysis);
        setLoading(false);
        sendResponse({ success: true });
      }
    };

    // Listen for postMessage (from frontend content script)
    window.addEventListener('message', handleMessage);
    
    // Only request page content data when app is NOT installed
    if (!isAppInstalled) {
      console.log('📄 [CompanyTest] App not installed, requesting page content data from extension...');
      window.parent.postMessage({ type: 'REQUEST_PAGE_CONTENT' }, '*');
    } else {
      console.log('📄 [CompanyTest] App is installed, skipping page content request');
    }
    
    return () => {
      window.removeEventListener('message', handleMessage);
      
    };
  }, [isAppInstalled]);

  // Extract IDs and type from URL
  useEffect(() => {
    const extractFromUrl = (url) => {
      const tenantMatch = url.match(/contacts\/(\d+)/);
      const companyMatch = url.match(/0-2\/(\d+)/);
      const contactMatch = url.match(/0-1\/(\d+)/);

      if (tenantMatch) {
        setTenantId(tenantMatch[1]);
      }

      if (companyMatch) {
        setObjectId(companyMatch[1]);
        setInsightType('company');
      } else if (contactMatch) {
        setObjectId(contactMatch[1]);
        setInsightType('contact');
      }
    };

    const url = new URLSearchParams(window.location.search);
    const urlParam = url.get('url');
    console.log('urlParam', urlParam);
    if (urlParam) {
      extractFromUrl(urlParam);
    }
  }, []);

  // Fetch signals whenever the company objectId is known
  useEffect(() => {
    if (!objectId || insightType !== 'company') return;

    setSignalsLoading(true);
    setSignalsError(null);

    apiService.getSignalsBySourceId(objectId)
      .then((res) => {
        if (res.data?.success) {
          setSignalsData(res.data.data || []);
        } else {
          setSignalsError(res.data?.message || 'Failed to load signals');
        }
      })
      .catch((err) => {
        // 404 simply means no signals yet — not an error worth surfacing loudly
        if (err.response?.status === 404) {
          setSignalsData([]);
        } else {
          setSignalsError(err.response?.data?.message || 'Failed to load signals');
        }
      })
      .finally(() => setSignalsLoading(false));
  }, [objectId, insightType]);

  // Fetch insights when IDs and type are set AND (page content is available OR app is installed)
  useEffect(() => {
    const canFetchData = tenantId && objectId && insightType;
    const hasRequiredData = isAppInstalled || pageContent;
    
    if (canFetchData && hasRequiredData) {
      console.log('[CompanyTest] Starting API call for:', { 
        tenantId, 
        objectId, 
        insightType,
        isAppInstalled,
        pageContentLength: pageContent?.length 
      });
      setLoading(true);
      setError(null);
      setInsightData(null);
      
      // Conditionally call the appropriate API based on isAppInstalled
      const insightsPromise = isAppInstalled
        ? apiService.getCompanyInsights(objectId, tenantId)
        : apiService.getCompanyInsightsWithoutApp(objectId, pageContent);
      
      insightsPromise
        .then((result) => {
          console.log('[CompanyInsights] API response:', result);
          
          // Handle API error responses (success: false)
          if (result.data && result.data.success === false) {
            const errorMessage = result.data.message || 'Failed to fetch company insights';
            setError(errorMessage);
            return;
          }
          
          if (result.data.success && result.data.data) {
            // Handle array response (case "first")
            if (Array.isArray(result.data.data) && result.data.data.length > 0) {
              const first = result.data.data[0];
              if (first.insight) {
                // The API returns data in first.insight.aura_analysis format
                // We need to flatten this structure for the tab components
                const flattenedData = {
                  ...first.insight.aura_analysis,
                  // Also include any other properties from first.insight that might be needed
                  ...first.insight
                };
                setInsightData(flattenedData);
              } else {
                setInsightData(first);
              }
            } 
            // Handle object response (case "second" or "third")
            else if (typeof result.data.data === 'object' && Object.keys(result.data.data).length > 0) {
              // Check if it's the "third" case format with aura_analysis
              if (result.data.data.aura_analysis) {
                // Flatten the aura_analysis structure for the tab components
                const flattenedData = {
                  ...result.data.data.aura_analysis,
                  // Also include any other properties from the data object
                  ...result.data.data
                };
                setInsightData(flattenedData);
              } else {
                // Handle other object formats
                setInsightData(result.data.data);
              }
            } else {
              setError('No company data found');
            }
          } else if (result.data.success && result.data.case === "running") {
            // Handle "running" case - show the processing message
            setError(result.data.message || 'Aura is currently processing the company');
          } else {
            setError('No company data found');
          }
        })
        .catch((e) => {
          console.error('API Error:', e);
          // Check if the error has response data with a specific message
          if (e.response && e.response.data && e.response.data.message) {
            setError(e.response.data.message);
          } else {
            setError('Failed to fetch company insights');
          }
        })
        .finally(() => setLoading(false));
    }
  }, [tenantId, objectId, insightType, isAppInstalled, pageContent]);

  // Render content based on active tab
  const renderTabContent = () => {
    // Signals tab has its own loading/empty states and doesn't need insightData
    if (activeTab === 'signals') {
      return (
        <SignalsTab
          signalsData={signalsData}
          signalsLoading={signalsLoading}
          signalsError={signalsError}
          companySourceId={objectId}
          Section={Section}
        />
      );
    }

    if (!insightData) return null;

    switch (activeTab) {
      case 'overview':
        return <OverviewTab insightData={insightData} Section={Section} />;
      case 'stakeholders':
        return <StakeholdersTab insightData={insightData} Section={Section} />;
      case 'value':
        return <ValueTab insightData={insightData} Section={Section} />;
      case 'risks':
        return <RisksTab insightData={insightData} Section={Section} />;
      case 'actions':
        return <ActionsTab insightData={insightData} Section={Section} />;
      case 'timeline':
        return <TimelineTab insightData={insightData} Section={Section} />;
      case 'competitive':
        return <CompetitiveTab insightData={insightData} Section={Section} />;
      case 'expansion':
        return <ExpansionTab insightData={insightData} Section={Section} />;
      case 'research':
        return (
          <ResearchTab 
            insightData={insightData} 
            Section={Section}
            researchData={researchData}
            setResearchData={setResearchData}
            researchLoading={researchLoading}
            setResearchLoading={setResearchLoading}
            researchError={researchError}
            setResearchError={setResearchError}
            hasSearched={hasSearched}
            setHasSearched={setHasSearched}
          />
        );
      default:
        return <OverviewTab insightData={insightData} Section={Section} />;
    }
  };

  if (loading) return <Loading />;

  // If there's an insight error but we're on the signals tab, stay inside the
  // sidepanel so the user can still navigate to Signals freely.
  if (error && activeTab !== 'signals') {
    return (
      <SidepanelTest insightData={insightData} onTabChange={setActiveTab}>
        <div className="bg-gray-50 font-sans text-gray-900 antialiased min-h-screen">
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
              {error}
            </div>
          </div>
        </div>
      </SidepanelTest>
    );
  }

  return (
    <SidepanelTest insightData={insightData} onTabChange={setActiveTab}>
      <div className="bg-gray-50 font-sans text-gray-900 antialiased min-h-screen">
        {/* Main Content */}
        <div className="p-6">
          {/* Tab Content */}
          <div className="animate-fadeIn">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </SidepanelTest>
  );
};

export default CompanyTest;
